import type { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useLoaderData } from "react-router";
import { SITE_NAME, API_BASE } from "@/lib/constants";
import { safeFetchJson } from "@/lib/fetch-json";
import type { EnsResult } from "@/lib/api";
import EnsPage from "@/pages/EnsPage";

export async function loader({ params }: LoaderFunctionArgs) {
  const name = params.name!;
  const ens = await safeFetchJson<EnsResult>(`${API_BASE}/ens/${encodeURIComponent(name)}`, {
    timeoutMs: 3000,
  });
  return { name, ens };
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const name = data?.name ?? "";
  const ens = data?.ens;
  const origin = "https://loadlogo.com";

  if (ens && !ens.error) {
    const description = `ENS avatar for ${name}${ens.address ? ` (${ens.address.slice(0, 6)}...${ens.address.slice(-4)})` : ""}. ${ens.description || `Fetch ENS avatars via the ${SITE_NAME} API.`}`;
    return [
      { title: `${name} ENS Avatar | ${SITE_NAME}` },
      { name: "description", content: description },
      { property: "og:title", content: `${name} ENS Avatar | ${SITE_NAME}` },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${origin}/og/ens/${name}` },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:url", content: `${origin}/ens/${name}` },
      { property: "og:site_name", content: SITE_NAME },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${name} ENS Avatar | ${SITE_NAME}` },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: `${origin}/og/ens/${name}` },
      { tagName: "link", rel: "canonical", href: `${origin}/ens/${name}` },
    ];
  }

  return [
    { title: `${name} ENS Avatar | ${SITE_NAME}` },
    {
      name: "description",
      content: `ENS avatar and profile for ${name}. Fetch ENS avatars via the ${SITE_NAME} API.`,
    },
    { property: "og:title", content: `${name} ENS Avatar | ${SITE_NAME}` },
    {
      property: "og:description",
      content: `ENS avatar and profile for ${name}. Fetch ENS avatars via the ${SITE_NAME} API.`,
    },
    { property: "og:type", content: "website" },
    { property: "og:image", content: `${origin}/og/ens/${name}` },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:url", content: `${origin}/ens/${name}` },
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { tagName: "link", rel: "canonical", href: `${origin}/ens/${name}` },
  ];
};

export default function EnsRoute() {
  const { ens } = useLoaderData<typeof loader>();
  return <EnsPage serverData={ens} />;
}
